'use client';

import { useMemo } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useCreateMatter } from '@/lib/contexts/create-matter-context';
import {
  GETTING_STARTED_TOTAL_STEPS,
  getGettingStartedActions,
  useGettingStartedHydrated,
  useGettingStartedInitiated,
  useGettingStartedSetupFirmComplete,
  type GettingStartedStepKey
} from '@/lib/stores';

/**
 * Read-only progress snapshot, sourced exclusively from the Zustand
 * store. Use in navigation surfaces (icon-rail tooltip, sidebar
 * widget) — does not fetch profile or onboarding status.
 *
 * The hub page at /onboarding/get-started owns fetching and writes the
 * cache (via `useGettingStartedProgress`); everything else just reads.
 */
export function useGettingStartedSnapshot() {
  const initiated = useGettingStartedInitiated();
  const cachedSetupFirmComplete = useGettingStartedSetupFirmComplete();
  const hydrated = useGettingStartedHydrated();

  const setupFirmComplete = cachedSetupFirmComplete ?? false;

  const completedCount = useMemo(() => {
    const setupCount = setupFirmComplete ? 1 : 0;
    const checklistCount = initiated.filter(
      (key) => key !== 'setup-firm'
    ).length;
    return setupCount + checklistCount;
  }, [initiated, setupFirmComplete]);

  const percent = Math.round(
    (completedCount / GETTING_STARTED_TOTAL_STEPS) * 100
  );

  return {
    initiated,
    setupFirmComplete,
    completedCount,
    percent,
    isHydrating: !hydrated
  };
}

/**
 * Per-step click handlers. Performs the same navigation/modal action the
 * hub uses, so the navigation tooltip behaves identically to the checklist
 * row.
 *
 * Setup-firm marks initiated on click for the in-progress pill (its
 * completion verdict is profile-driven). Navigate also completes on
 * click since the tour is the navigation itself. The remaining action
 * steps (create-matter / intake-form / ai-scribe / notify-institutions)
 * complete from their own action surfaces, not from this click. See
 * LF-3143.
 *
 * Demo-matter destinations use the canonical demoId=1 path directly
 * — no fetch — matching the hub which also hardcodes demoId=1 for all
 * checklist demo actions and only fetches as a defensive existence
 * check.
 */
export function useGettingStartedStepActions() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { open: openCreateMatter } = useCreateMatter();

  const handleSetupFirmClick = () => {
    getGettingStartedActions().markInitiated('setup-firm');
    router.push('/onboarding');
  };

  const handleStepClick = (key: GettingStartedStepKey) => {
    if (key === 'setup-firm') {
      handleSetupFirmClick();
      return;
    }
    // Navigate completes on the immediate click. Other action items
    // complete from their own action surfaces (LF-3143).
    if (key === 'navigate') {
      getGettingStartedActions().markInitiated('navigate');
    }
    switch (key) {
      case 'navigate':
        router.push(`/matters/demo/1?tour=${key}`);
        return;
      case 'create-matter': {
        const params = new URLSearchParams(searchParams.toString());
        params.set('tour', key);
        router.push(`${pathname}?${params}`);
        openCreateMatter();
        return;
      }
      case 'intake-form':
        router.push(`/matters/demo/2/intake?tour=${key}`);
        return;
      case 'ai-scribe':
        router.push(`/matters/demo/2/recordings?tour=${key}`);
        return;
      case 'notify-institutions':
        router.push(`/matters/demo/1/discovery?tour=${key}`);
        return;
    }
  };

  return { handleSetupFirmClick, handleStepClick };
}
